import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ChargeUserDTO, fetchUserChargesRange } from '../../services/paymentService';
import { PaymentRows } from '../../components/payments/PaymentRows';
import { formatDate, formatMoney } from '../../utils/paymentFormatters';
import '../../styles/AccountTools.css';

const PAGE_SIZE = 10;

const Charges = () => {
  const { user } = useAuth();
  const actorId = user?.id || '';
  const [charges, setCharges] = useState<ChargeUserDTO[]>([]);
  const [page, setPage] = useState(0);
  const [isLastPage, setIsLastPage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const loadCharges = useCallback(
    async (nextPage = 0, append = false) => {
      if (!actorId) return;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError('');

      try {
        const now = new Date();
        const start = new Date(now);
        start.setDate(start.getDate() - 180);

        const response = await fetchUserChargesRange({
          userId: actorId,
          startISO: start.toISOString(),
          endISO: now.toISOString(),
          page: nextPage,
          size: PAGE_SIZE,
        });

        setCharges((prev) => {
          const combined = append ? [...prev, ...response.content] : response.content;
          return Array.from(new Map(combined.map((item) => [item.id, item])).values());
        });
        setPage(response.number ?? nextPage);
        setIsLastPage(response.last ?? true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load issued payments.');
        if (!append) setCharges([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [actorId]
  );

  useEffect(() => {
    loadCharges();
  }, [loadCharges]);

  if (!actorId) {
    return (
      <main className="tool-page">
        <div className="tool-card tool-empty">Sign in to view issued payments.</div>
      </main>
    );
  }

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <p className="tool-breadcrumb"><Link to="/account/payments">Payments &amp; Credits</Link> / Charges</p>
          <h1>Charges</h1>
          <p>Issued payments from the last 180 days.</p>
        </div>
        <button className="tool-btn-secondary" type="button" onClick={() => loadCharges()} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <div className="tool-message error">{error}</div>}

      {loading && charges.length === 0 ? (
        <div className="tool-loading">Loading issued payments...</div>
      ) : (
        <section className="tool-card">
          <PaymentRows
            rows={charges.map((item) => ({
              id: item.id,
              title: item.description || item.chargeItemCode || 'Payment',
              amount: formatMoney(item.amount, item.currency || 'USD'),
              meta: `${item.status || item.paymentStatus || 'Status unavailable'} - ${formatDate(item.createdAt)}`,
            }))}
            empty="No payments issued yet."
          />
          {!isLastPage && (
            <button
              className="tool-btn"
              type="button"
              style={{ marginTop: 16 }}
              onClick={() => loadCharges(page + 1, true)}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          )}
        </section>
      )}
    </main>
  );
};

export default Charges;
